import Service, { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import moment from 'moment';

import { DiscoveryListTask, SocialProfileType } from 'later/utils/constants';
import { SegmentEventTypes } from 'later/utils/constants/segment-events';
import { convert, timestamp } from 'later/utils/time-format';
import { PlanInterval } from 'shared/types/plans';

import type { ChatbotService } from './base-chatbot';
import type MavenService from './maven';
import type ZendeskService from './zendesk';
import type ArrayProxy from '@ember/array/proxy';
import type StoreService from '@ember-data/store';
import type AccountModel from 'later/models/account';
import type SocialProfileModel from 'later/models/social-profile';
import type SubscriptionModel from 'later/models/subscription';
import type AuthService from 'later/services/auth';
import type { Integration } from 'later/services/integrations-queue';
import type SegmentService from 'later/services/segment';
import type { SegmentIdentifyResponse } from 'later/services/segment';
import type SubscriptionsService from 'later/services/subscriptions';

export type SegmentString = string | null;

export interface UserAndAccountTraits {
  account_owner: boolean;
  activated_trial: boolean;
  active_trial: boolean;
  ai_credits_total: number;
  ai_credits_used: number;
  ai_credits_remaining: number;
  annual_user_initial_order_within_thirty_days: boolean;
  annual_user_renewed_over_thirty_days: boolean;
  billing_email: string;
  bttp_enabled: boolean;
  current_free_rollout: string;
  current_plan_rollout: string;
  email: string;
  experiment_variants_string: string;
  experiment_variants: string[] | [];
  first_thirty_days: boolean;
  force_migrated_at: string | null;
  has_business_profile: boolean;
  has_opened_chatbot_previously: boolean;
  initial_free_rollout: string;
  language: string;
  linkinbio_set_up: boolean;
  monthly_user_completed_order_over_seven_days: boolean;
  more_than_5_social_sets: boolean;
  number_of_social_sets: boolean;
  number_of_users: boolean;
  plan_interval: string;
  plan_name: string;
  plan_type: string;
  subscription_provider: string;
  support_segment: string;
  user_experiment_variants_string: string;
  user_experiment_variants: string[] | [];
  user_name: string;
  user_plan_name: string;
}

type SocialProfilePostInfo<T extends SocialProfileType> = {
  [K in `${T}_posts_total`]: 'not connected' | 'unlimited' | number;
} & {
  [K in `${T}_posts_used`]: 'not connected' | 'unlimited' | number;
} & {
  [K in `${T}_posts_remaining`]: 'not connected' | 'unlimited' | number;
};

type SocialProfileListAndInfo<T extends SocialProfileType> =
  | {
      [K in `${T}`]: string;
    }
  | ({
      [K in `${T}_profile_name`]: string;
    } & {
      [K in `${T}_profile_status`]:
        | 'Connected'
        | 'Connected: Enable Permissions'
        | 'Expired: Refresh Profile'
        | 'Connected: Enable Auto Publish';
    } & {
      [K in `${T}_follow_count`]: number;
    } & {
      [K in `${T}_profile_type`]: 'personal' | 'creator' | 'business';
    });

export default class ChatbotWrapperService extends Service implements Integration {
  @service declare auth: AuthService;
  @service declare segment: SegmentService;
  @service declare store: StoreService;
  @service declare subscriptions: SubscriptionsService;
  @service('chatbot/maven') declare maven: MavenService;
  @service('chatbot/zendesk') declare zendesk: ZendeskService;

  readonly name = 'chatbot';

  get currentChatbotService(): ChatbotService {
    return this.auth.currentAccount?.rolloutMavenChatbot ? this.maven : this.zendesk;
  }

  async setup(): Promise<void> {
    const userAndAccountTraits = await this.fetchUserAndAccountTraits();
    this.currentChatbotService.setup(userAndAccountTraits);
  }

  show(): void {
    this.currentChatbotService.show();
  }

  hide(): void {
    this.currentChatbotService.hide();
  }

  open(location: SegmentString = null, page: SegmentString = null): void {
    this.segment.track(SegmentEventTypes.UserOpenedChatbot, {
      location,
      active_trial: !!this.hasActiveTrial,
      page
    });

    if (!this.hasOpenedChatbotPreviously) {
      const item = this.store.createRecord('discovery-list-task', {
        identifier: DiscoveryListTask.OpenedChatbot,
        completedTime: timestamp(),
        user: this.auth.currentUserModel
      });
      item.save();
    }

    this.currentChatbotService.open();
  }

  close(): void {
    this.currentChatbotService.close();
  }

  reset(): void {
    this.currentChatbotService.reset();
  }

  get hasActiveTrial(): boolean {
    return this.subscriptions.hasActiveTrial;
  }

  get hasOpenedChatbotPreviously(): boolean {
    return isPresent(
      this.store.peekAll('discovery-list-task').find(({ identifier }) => identifier === DiscoveryListTask.OpenedChatbot)
    );
  }

  get subscription(): SubscriptionModel | Record<string, never> {
    return this.subscriptions.subscription;
  }

  #getSocialProfilePostInfo<T extends SocialProfileType>(
    profileType: T,
    profiles: ArrayProxy<SocialProfileModel>
  ): SocialProfilePostInfo<T> {
    const hasProfile = profiles.filter((profile) => profile.profileType === profileType).length > 0;

    const postsLeftForSocialProfile = `${profileType}PostsLeft` as keyof AccountModel;
    const hasUnlimitedPosts = !this.auth.currentAccount.maxPosts;

    let max: string | number = this.auth.currentAccount.maxPosts || 0;
    let postsLeft: string | number = (this.auth.currentAccount[postsLeftForSocialProfile] as number) || 0;
    let postsUsed: string | number = max - postsLeft;

    if (!hasProfile) {
      max = 'not connected';
      postsLeft = 'not connected';
      postsUsed = 'not connected';
    } else if (hasUnlimitedPosts) {
      max = 'unlimited';
      postsLeft = 'unlimited';
      postsUsed = 'unlimited';
    }

    const obj = {
      [`${profileType}_posts_total`]: max,
      [`${profileType}_posts_used`]: postsUsed,
      [`${profileType}_posts_remaining`]: postsLeft
    } as SocialProfilePostInfo<T>;
    return obj;
  }

  #getSocialProfileListAndInfo<T extends SocialProfileType>(
    socialProfileType: T,
    socialProfiles: ArrayProxy<SocialProfileModel>
  ): SocialProfileListAndInfo<T> {
    const selectedSocialProfiles = socialProfiles.filter(
      (socialProfile: SocialProfileModel) => socialProfile.profileType === socialProfileType
    );

    if (selectedSocialProfiles.length === 0) {
      return {
        [`${socialProfileType}`]: 'not connected'
      } as SocialProfileListAndInfo<T>;
    }

    if (selectedSocialProfiles.length === 1) {
      const {
        canSelectAutoPublish,
        defaultAutoPublish: hasEnabledAutoPublish,
        displayName,
        followedBy,
        hasInstagramAutoPublishScope,
        hasTiktokAutoPublishScope,
        hasUnconnectedProfessional,
        isBusiness,
        isCreator,
        isInstagram,
        isLinkedin,
        isMissingLinkedinPermissions,
        isTiktok,
        isTwitter,
        needsProfessionalTokenRefresh,
        needsRefresh
      } = selectedSocialProfiles[0];
      let socialProfileName = '';
      let profileStatus = 'Connected';
      let profileType = 'personal';

      if (needsRefresh) {
        profileStatus = 'Expired: Refresh Profile';
      } else if (!hasEnabledAutoPublish && canSelectAutoPublish) {
        profileStatus = 'Connected: Enable Auto Publish';
      } else if (
        (isMissingLinkedinPermissions && isLinkedin) ||
        (!hasInstagramAutoPublishScope && isInstagram) ||
        (!hasTiktokAutoPublishScope && isTiktok)
      ) {
        profileStatus = 'Connected: Enable Permissions';
      } else if (isCreator && (hasUnconnectedProfessional || needsProfessionalTokenRefresh)) {
        profileStatus = 'Connected: Enable Creator Profile';
      }

      if (isCreator) {
        profileType = 'creator';
      } else if (isBusiness) {
        profileType = 'business';
      }

      if (isInstagram || isTiktok || isTwitter) {
        socialProfileName = displayName;
      } else {
        socialProfileName = `${socialProfileType}/${displayName}`;
      }

      return {
        [`${socialProfileType}_profile_name`]: socialProfileName,
        [`${socialProfileType}_profile_status`]: profileStatus,
        [`${socialProfileType}_follow_count`]: followedBy,
        [`${socialProfileType}_profile_type`]: profileType
      } as SocialProfileListAndInfo<T>;
    }

    return {
      [`${socialProfileType}`]: `${selectedSocialProfiles.length} connected`
    } as SocialProfileListAndInfo<T>;
  }

  #getSubscriptionInfo(planInterval: string): {
    annual_user_initial_order_within_thirty_days: boolean;
    annual_user_renewed_over_thirty_days: boolean;
    monthly_user_completed_order_over_seven_days: boolean;
  } {
    let annual_user_initial_order_within_thirty_days = false;
    let annual_user_renewed_over_thirty_days = false;
    let monthly_user_completed_order_over_seven_days = false;

    if (this.subscription && this.subscription.isActive) {
      const lastPaymentDate = this.subscription.planModifiedMoment;
      const daysSinceLastPayment = moment().diff(lastPaymentDate, 'days');
      const daysSinceSubscriptionRenewal = moment().diff(this.subscription?.renewalDate, 'days');

      annual_user_initial_order_within_thirty_days =
        planInterval === PlanInterval.Yearly &&
        daysSinceLastPayment < 30 &&
        moment.unix(this.subscription?.createdAt).year() === moment().year();

      annual_user_renewed_over_thirty_days = planInterval === PlanInterval.Yearly && daysSinceSubscriptionRenewal > 30;

      monthly_user_completed_order_over_seven_days = planInterval === PlanInterval.Monthly && daysSinceLastPayment > 7;
    }

    return {
      annual_user_initial_order_within_thirty_days,
      annual_user_renewed_over_thirty_days,
      monthly_user_completed_order_over_seven_days
    };
  }

  async fetchUserAndAccountTraits(): Promise<UserAndAccountTraits | undefined> {
    let userAndAccountTraits: SegmentIdentifyResponse;

    try {
      userAndAccountTraits = await this.segment.fetchUserAndAccountTraits.perform();
    } catch {
      return;
    }

    const { user, account } = userAndAccountTraits;

    const {
      activated_trial,
      active_trial,
      number_of_ai_credits: ai_credits_total,
      number_of_ai_credits_used: ai_credits_used,
      number_of_ai_credits_left: ai_credits_remaining,
      billing_email,
      bttp_enabled,
      created_at: account_created_at,
      current_free_rollout,
      current_plan_rollout,
      experiment_variants,
      force_migrated_at,
      has_business_profile,
      initial_free_rollout,
      plan_interval,
      plan_name,
      plan_type,
      subscription_provider_name
    } = account;

    const {
      account_owner,
      email,
      has_enabled_linkinbio: linkinbio_set_up,
      name: user_name,
      plan_name: user_plan_name,
      experiment_variants: user_experiment_variants,
      language,
      support_segment,
      number_of_social_sets,
      number_of_users
    } = user;

    const has_opened_chatbot_previously = this.hasOpenedChatbotPreviously;
    const more_than_5_social_sets = number_of_social_sets > 5;
    const first_thirty_days = Boolean(
      account_created_at && new Date(account_created_at) > new Date(Date.now() - convert.days(30).toMilliseconds())
    );

    const subscription_provider = subscription_provider_name === 'Apple' ? 'iOS' : 'Web';

    const socialProfiles = await this.store.findAll('social-profile');

    const profilePosts = Object.assign(
      {},
      ...Object.values(SocialProfileType).map((profile) => this.#getSocialProfilePostInfo(profile, socialProfiles))
    );

    const profileListAndInfo = Object.assign(
      {},
      ...Object.values(SocialProfileType).map((profile) => this.#getSocialProfileListAndInfo(profile, socialProfiles))
    );

    const subscriptionInfo = this.#getSubscriptionInfo(plan_interval ?? '');

    // for grandfathered livechat
    const can_live_chat = this.auth.currentAccount.canChat;

    return {
      account_owner,
      activated_trial,
      active_trial,
      ai_credits_total,
      ai_credits_used,
      ai_credits_remaining,
      billing_email,
      bttp_enabled,
      can_live_chat,
      current_free_rollout,
      current_plan_rollout,
      email,
      experiment_variants,
      first_thirty_days,
      force_migrated_at,
      has_business_profile,
      has_opened_chatbot_previously,
      initial_free_rollout,
      language,
      linkinbio_set_up,
      more_than_5_social_sets,
      number_of_social_sets,
      number_of_users,
      plan_interval,
      plan_name,
      plan_type,
      ...profileListAndInfo,
      ...profilePosts,
      ...subscriptionInfo,
      subscription_provider,
      support_segment,
      user_experiment_variants,
      user_name,
      user_plan_name
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    'chatbot/chatbot-wrapper': ChatbotWrapperService;
  }
}
